import { isMobile } from '@mightybyte/rnw.utils.device-info';
import React, { useEffect, useMemo, useState } from 'react';
import { GAME_TYPES_CONFIG, GAME_TYPES_TYPE } from '../../../constants/constants';
import {
  EJourneyGameBadgeType,
  EStageId,
  EStageType,
  getBeeBotByDifficulty,
} from '../../../utils/journeysUtils';
import { PopupV2 } from '../PopupV2';
import { ApolloError } from '@apollo/client';
import { useSignedInContext } from '../../../context/SignedInContext';
import { getErrorText } from '../../../utils/errors';
import getUserName from '../../../utils/userUtils';
import { GameMode } from '../../screens/GameScreen';
import {
  AIDifficultyLevel,
  useJoinAIGameMutation,
} from '../../screens/PlayWithAI/useJoinAiGameMutation.gql';
import {
  useGameEventSubscription,
  EventTypeGameEventType,
} from '../InviteToGame/useGameEventSubscription.gql';
import { GameStatus } from '../InviteToGame/useJoinGameByCodeMutation.gql';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { View, Text, StyleSheet, Alert } from 'react-native';
import ButtonGeneral from '../ButtonGeneral';
import { YourPoints } from '../YourPoints';
import { RibbonBadge } from '../../../resources/svgComponents/badgeComponents/RibbonBadge';
import { useUpsertJourneyHistoryMutation } from '../../../graphql/journey/useUpsertHistoryMutation.gql';
import { useUpdateJourneyStageMutation } from '../../../graphql/journey/useUpdateJourneyMutation.gql';
import { useUpsertJourneyGameBadgesMutation } from '../../../graphql/journey/useUpsertJourneyGameBadgesMutation.gql';
import { useUpsertEarnedBadgeMutation } from '../../../graphql/journey/useUpsertEarnedBadgeMutation.gql';
import { useGetJourneyGameBadgeByUserAndGameQuery } from '../../../graphql/journey/getJourneyGameBadgesByUserAnGame.gql';
import { useIncrementUserPointMutation } from '../../../graphql/useIncrementUserPointMutation.gql';

const getDifficultyLevelMessage = (level: AIDifficultyLevel, gameType: GAME_TYPES_TYPE) => {
  switch (level) {
    case AIDifficultyLevel.EASY:
      return {
        description: `You beat your first Boss!\n You’re well on your way to mastering ${GAME_TYPES_CONFIG[gameType].operation}!`,
        level: 1,
      };
    case AIDifficultyLevel.MEDIUM:
      return {
        description: `You beat Bee-${getBeeBotByDifficulty(
          level,
        )}\n You’re well on your way to mastering ${GAME_TYPES_CONFIG[gameType].operation}!`,
        level: 2,
      };
    case AIDifficultyLevel.HARD:
      return {
        description: `You beat Bee-${getBeeBotByDifficulty(
          level,
        )} \n You’re well on your way to mastering ${GAME_TYPES_CONFIG[gameType].operation}!`,
        level: 3,
      };

    case AIDifficultyLevel.EXTREME:
      return {
        description: `You beat Bee-${getBeeBotByDifficulty(level)} \n He's the final Boss!`,
        level: 4,
      };
    default:
      return {
        description: 'Unknown',
        level: 0,
      };
  }
};

type TJourneyResultPopupProps = {
  gameType: GAME_TYPES_TYPE;
  navigation: any;
  route: any;
  visible: boolean;
  setVisible: (value: boolean) => void;
  isWinner: boolean;
  isDraw: boolean;
  isError?: boolean;
  beeBot?: string;
};

export default function JourneyResultPopup(props: TJourneyResultPopupProps) {
  const { gameType, navigation, route, visible, setVisible, isWinner, isDraw, isError, beeBot } =
    props;

  const [upsertJourneyHistory, { loadingUpdate }] = useUpsertJourneyHistoryMutation();
  const [updateJourneyStage] = useUpdateJourneyStageMutation();
  const [upsertJourneyGameBadges] = useUpsertJourneyGameBadgesMutation();
  const [upsertEarnedBadge] = useUpsertEarnedBadgeMutation();

  const [gameDifficultyLevel, setGameDifficultyLevel] = useState(
    route.params.difficulty || AIDifficultyLevel.EASY,
  );

  const { user } = useSignedInContext();
  const userId = user?.id;

  const name = getUserName(user);

  const journeyId = route.params?.journeyId;
  const stageId = route.params?.stageId as EStageId;

  const [bossUnlocked, setBossUnlocked] = useState(route.params.bossUnlocked);
  const [incrementUserPoint] = useIncrementUserPointMutation();

  const nectarPointsAwarded = bossUnlocked ? 5 : 3;

  const [joinGame, { loading, error }] = useJoinAIGameMutation();

  const { data: gameEventSubscription } = useGameEventSubscription({
    onSubscriptionComplete: () => console.log('PlayWithAI completed!'),
  });
  const { data: journeyGameBadgeData } = useGetJourneyGameBadgeByUserAndGameQuery({
    variables: {
      userId: userId || '',
      gameType,
    },
    fetchPolicy: 'network-only',
    skip: !userId,
  });

  useEffect(() => {
    const awardPoints = async () => {
      if (isWinner && userId) {
        try {
          const result = await incrementUserPoint({
            variables: { points: nectarPointsAwarded },
          });

          if (!result.data?.incrementUserPoint) {
            throw new Error('Failed to increment user points');
          }

          console.log(`✅ ${nectarPointsAwarded} points awarded to user ${userId}`);
        } catch (err) {
          console.error('❌ Failed to award points:', err);
          Alert.alert('Error', 'Failed to award nectar points.');
        }
      }
    };

    awardPoints();
  }, [isWinner, userId, nectarPointsAwarded]);

  useEffect(() => {
    const awardBadge = async () => {
      if (isWinner && bossUnlocked && userId) {
        try {
          const difficultyMessage = getDifficultyLevelMessage(gameDifficultyLevel, gameType);
          const levelBadgeValue = String(difficultyMessage.level);

          const existingDoc = journeyGameBadgeData?.journeyGameBadgeByUserAndGame;

          if (existingDoc?._id) {
            // 📝 2. Upsert a single earned badge into existing doc
            await upsertEarnedBadge({
              variables: {
                id: existingDoc._id,
                badge: {
                  type: EJourneyGameBadgeType.Level,
                  value: levelBadgeValue,
                  earnedAt: new Date().toISOString(),
                },
              },
            });
          } else {
            // 🆕 3. Create a new doc with the earned badge
            await upsertJourneyGameBadges({
              variables: {
                userId,
                gameType,
                data: {
                  earnedBadges: [
                    {
                      type: EJourneyGameBadgeType.Level,
                      value: levelBadgeValue,
                      earnedAt: new Date().toISOString(),
                    },
                  ],
                },
              },
            });
          }

          console.log(`✅ Badge Level ${levelBadgeValue} awarded!`);
        } catch (err) {
          console.error('❌ Failed to award badge:', err);
        }
      }
    };

    awardBadge();
  }, [isWinner, bossUnlocked, userId, gameDifficultyLevel, gameType]);
  useEffect(() => {
    if (
      gameEventSubscription?.gameEvent.eventType === EventTypeGameEventType.ON_GAME_START &&
      gameEventSubscription.gameEvent.game.status !== GameStatus.FINISHED
    ) {
      navigation.replace('Game', {
        mode: GameMode.PRACTICE,
        difficulty: gameDifficultyLevel,
        journey: true,
        stageId: stageId,
        journeyId,
        gameType: gameType,
        ...(bossUnlocked && { bossUnlocked: true }),
        game: gameEventSubscription.gameEvent.game,
      });
      //close window
      setVisible(false);
    }
    if (
      gameEventSubscription?.gameEvent &&
      gameEventSubscription.gameEvent.game.status === GameStatus.FINISHED
    ) {
      console.warn('finished game ignored!');
    }
  }, [gameDifficultyLevel, gameEventSubscription?.gameEvent, navigation]);

  const handleJoinGameError = (err: ApolloError) => {
    setHasError({ popup: true, visible: true, text: getErrorText(err) });
  };

  const [hasError, setHasError] = useState({
    visible: false,
    popup: false,
    text: '',
  });

  const handleJoinGame = () => {
    AsyncStorage.setItem('playing-name', name);
    joinGame({
      variables: {
        userName: name,
        difficultyLevel: gameDifficultyLevel,
        aiName: bossUnlocked
          ? `Bee-${getBeeBotByDifficulty(gameDifficultyLevel)}`
          : `${getBeeBotByDifficulty(gameDifficultyLevel)}'s Bot`,
        gameType: gameType,
      },
    }).catch(err => {
      handleJoinGameError(err);
    });
  };

  const handleJoinGameBoss = () => {
    AsyncStorage.setItem('playing-name', name);
    joinGame({
      variables: {
        userName: name,
        difficultyLevel: gameDifficultyLevel,
        aiName: `Bee-${getBeeBotByDifficulty(gameDifficultyLevel)}`,

        gameType: gameType,
      },
    }).catch(err => {
      handleJoinGameError(err);
    });
  };

  const handlePress = () => {
    handleJoinGame();
    // onProceed();
  };

  const backToLJourneyLobby = () => {
    navigation.reset({
      index: 0, // start at the only screen in the stack
      routes: [
        {
          name: 'LobbyJourney',
          params: {
            gameType: gameType,
            journey: true,
          },
        },
      ],
    });
  };

  const handleWin = async () => {
    try {
      // 🔹 Update journey history
      await upsertJourneyHistory({
        variables: {
          journeyId,
          history: {
            stageId,
            type: EStageType.Level,
            completedAt: new Date().toISOString(),
            details: JSON.stringify({
              bossDone: true,
            }),
          },
        },
      });
      await updateJourneyStage({
        variables: {
          id: journeyId,
          currentStage: stageId,
        },
      });

      console.log('✅ Success', 'Level updated successfully!');
      setBossUnlocked(false);
      backToLJourneyLobby();
    } catch (err) {
      console.error(err);
      console.log('❌ Error', 'Failed to submit journey update.');
    }
  };

  const elementValues = useMemo(() => {
    if (isWinner) {
      return {
        title: 'You Won!',
        subtitle: 'Congratulations!',
        description: bossUnlocked
          ? `${getDifficultyLevelMessage(gameDifficultyLevel, gameType).description}`
          : `You beat one of the Bee-${beeBot}s' bots! \n Play again for more practice or are you ready to take on Boss Bee-${beeBot}?`,
        buttonText1: 'Practice Again',
        buttonText2: bossUnlocked ? 'Go to Learning Journey' : `Challenge Bee-${beeBot}!`,
        buttonFunction1: handlePress,
        buttonFunction2: bossUnlocked
          ? handleWin
          : () => {
              setBossUnlocked(true);
              handleJoinGameBoss();
            },
      };
    }
    if (isDraw) {
      return {
        title: 'It’s a Draw!',
        subtitle: 'This round is a draw',
        description: `Play again and  practice more, you will be able proceed  ${
          bossUnlocked
            ? `to the next level once you win against Boss Bee-${beeBot}`
            : `when you beat the bee-bots`
        }.`,
        buttonText1: 'Go to Learning Journey',
        buttonText2: 'Try Again',
        buttonFunction1: backToLJourneyLobby,
        buttonFunction2: handlePress,
      };
    }
    return isError
      ? {
          title: 'Game Already Finished',
          subtitle: 'This game has already ended',
          description: `Play again and  practice more, you will be able proceed  ${
            bossUnlocked
              ? `to the next level once you win against Boss Bee-${beeBot}`
              : `when you beat the bee-bots`
          }.`,
          buttonText1: 'Go to Learning Journey',
          buttonText2: 'Try Again',
          buttonFunction1: backToLJourneyLobby,
          buttonFunction2: handlePress,
        }
      : {
          title: 'You Lost',
          subtitle: 'You lost this round!',
          description: `Play again and  practice more, you will be able proceed  ${
            bossUnlocked
              ? `to the next level once you win against Boss Bee-${beeBot}`
              : `when you beat the bee-bots`
          }.`,
          buttonText1: 'Go to Learning Journey',
          buttonText2: 'Try Again',
          buttonFunction1: backToLJourneyLobby,
          buttonFunction2: handlePress,
        };
  }, [isWinner, isDraw, isError, beeBot, bossUnlocked, visible]);

  return (
    <>
      <PopupV2
        title={elementValues.title}
        visible={visible}
        setVisible={setVisible}
        height={
          isMobile ? (isWinner && bossUnlocked ? 480 : 387) : isWinner && bossUnlocked ? 500 : 426
        }
        hexagonHeight={isMobile ? 54 : 62}
        hexagonWidth={isMobile ? 252 : 346}
        hideCloseButton={true}
        closeOnBackdropPress={false}
        hexagonColor={!isWinner ? ['#747474', '#1F1F1F'] : undefined}
      >
        <View style={[styles.card]}>
          <View style={[styles.content, { marginTop: isMobile ? 33 : 26 }]}>
            <Text style={[styles.subtitle, { fontSize: isMobile ? 18 : 24 }]}>
              {elementValues.subtitle}
            </Text>
            <Text style={[styles.description, { fontSize: isMobile ? 14 : 16 }]}>
              {elementValues.description}
            </Text>
            {isWinner && bossUnlocked && (
              <View style={styles.badgeContainer}>
                <Text style={styles.badgeText}>
                  {`Level ${
                    getDifficultyLevelMessage(gameDifficultyLevel, gameType).level
                  } Badge Earned`}
                </Text>
                <View style={styles.ribbonContainer}>
                  <RibbonBadge
                    number={getDifficultyLevelMessage(gameDifficultyLevel, gameType).level}
                  />
                </View>
              </View>
            )}
            {isWinner && (
              <View style={styles.pointsContainer}>
                <YourPoints text="Nectar Earned" points={nectarPointsAwarded} pointSymbol="+" />
              </View>
            )}
          </View>

          <View
            style={[
              styles.buttonContainer,
              {
                height: isMobile ? 80 : 102,
                gap: isMobile ? 8 : 12,
              },
            ]}
          >
            {!(isWinner && bossUnlocked) && (
              <ButtonGeneral
                onPress={() => {
                  setVisible(false);
                  elementValues.buttonFunction1();
                }}
                title={elementValues.buttonText1}
                height={isMobile ? 48 : 58}
                width={isMobile ? (isWinner ? 151 : 196) : 201}
                fontSize={isMobile ? 12 : 15}
                colors={['#FEDD88', '#FEDD88']}
                nameContainerStyle={{
                  borderColor: 'white',
                  borderWidth: 2,
                  marginRight: isMobile ? 10 : 0,
                }}
              />
            )}
            <ButtonGeneral
              onPress={() => {
                setVisible(false);
                elementValues.buttonFunction2();
              }}
              title={elementValues.buttonText2}
              height={isMobile ? 48 : 58}
              width={
                isMobile
                  ? isWinner
                    ? bossUnlocked
                      ? 200
                      : 170
                    : 104
                  : isWinner && bossUnlocked
                  ? 411
                  : 201
              }
              fontSize={isMobile ? 12 : 15}
            />
          </View>
        </View>
      </PopupV2>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    overflow: 'hidden',
    flexDirection: 'column',
    height: '100%',
  },

  content: {
    alignItems: 'center',
    paddingHorizontal: 60,
    paddingTop: 30,
    flex: 1,
  },

  subtitle: {
    fontFamily: 'Secular One',
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 8,
    color: '#000000',
  },
  description: {
    fontFamily: 'Secular One',
    color: '#453B3B',
    textAlign: 'center',
    fontWeight: '700',
  },
  buttonContainer: {
    justifyContent: 'center',
    backgroundColor: '#FEDD88',
    alignItems: 'center',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    flexDirection: 'row',
  },

  pointsContainer: {
    width: 233,
    marginTop: 18,
    justifyContent: 'center',
  },
  badgeContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  badgeText: {
    fontWeight: '400',
    fontSize: 16,
    color: '#8C8C8C',
  },
  ribbonContainer: {
    marginTop: 10,
  },
});
